import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { checkAddPermission, checkEditPermission, checkDeletePermission, handleImportClickFile, handleUpdateAllCheckbox, handleUpdateDataCheckbox, handleExportFile } from "../../../Utils/helper";
import { getApi, postApi, putApi, deleteApi } from "../../../Services/apiService";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import Import from "../../../Utils/import";
import { exportShippingMethods } from "../../../Services/importExport";

function Sales() {
  const navigate = useNavigate()
  const [methodType, setMethodType] = useState('');
  const { data } = useSelector((state) => state.roleData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sellerToDelete, setSellerToDelete] = useState(null);
  const [sellerToEdit, setSellerToEdit] = useState(null);
  const userData = JSON.parse(localStorage.getItem('userDetail'))

  // Form states
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    is_active: 1
  });
  const [errors, setErrors] = useState({
    email: '',
    name: ''
  });
  const [touched, setTouched] = useState({
    email: false,
    name: false
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importErrors, setImportErrors] = useState({
    file: '',
    general: ''
  });
  // Fetch sellers
  const fetchSellers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getApi(`/sellers?search=${encodeURIComponent(searchQuery)}&per_page=100`);

      if (response?.success) {
        setSellers(response.data.sellers?.data || []);
      } else {
        setError(response?.msg || 'Failed to fetch sellers');
      }
    } catch (error) {
      setError(error.message || 'Failed to fetch sellers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSellers();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (touched[name]) {
      validateField(name, value);
    }
  };

  // Validate single field
  const validateField = (fieldName, value) => {
    let error = '';

    if (!value.trim()) {
      error = `${fieldName} is required`;
    } else if (fieldName === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      error = 'Please enter a valid email address';
    } else if (fieldName === 'name' && value.length > 50) {
      error = 'Name should be less than 50 characters';
    }

    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return !error;
  };

  // Handle blur
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, value);
  };

  // Validate entire form
  const validateForm = () => {
    let isValid = true;
    const newTouched = {
      email: true,
      name: true
    };
    setTouched(newTouched);

    Object.keys(formData).forEach(key => {
      if (key !== 'is_active') {
        const valid = validateField(key, formData[key]);
        if (!valid) isValid = false;
      }
    });

    return isValid;
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      is_active: 1
    });
    setErrors({
      email: '',
      name: ''
    });
    setTouched({
      email: false,
      name: false
    });
  };

  // Add new seller
  const handleAddSeller = async () => {
    if (!validateForm()) {
      // toast.error('Please fix the validation errors');
      return;
    }

    try {
      setIsLoading(true);
      const response = await postApi('/sellers', formData);

      if (response?.success) {
        toast.success(response.msg || 'Seller added successfully');
        await fetchSellers();
        setMethodType('');
        resetForm();
      } else {
        toast.error(response?.msg || 'Failed to add seller');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to add seller';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Set up edit form
  const setupEditForm = (seller) => {
    setFormData({
      email: seller.email,
      name: seller.name,
      is_active: seller.is_active
    });
    setMethodType('edit');
    setSellerToEdit(seller);
  };

  // Update seller
  const handleUpdateSeller = async () => {
    if (!validateForm() || !sellerToEdit) {
      // toast.error('Please fix the validation errors');
      return;
    }

    try {
      setIsLoading(true);
      const response = await putApi(`/sellers/${sellerToEdit.id}`, formData);

      if (response?.success) {
        toast.success(response.msg || 'Seller updated successfully');
        await fetchSellers();
        setMethodType('');
        resetForm();
      } else {
        toast.error(response?.msg || 'Failed to update seller');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to update seller';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete confirmation
  const confirmDeleteSeller = async () => {
    if (!sellerToDelete) return;

    try {
      setIsLoading(true);
      const response = await deleteApi(`/sellers/${sellerToDelete.id}`);

      if (response?.success) {
        toast.success(response.msg || 'Seller deleted successfully');
        await fetchSellers();
      } else {
        toast.error(response?.msg || 'Failed to delete seller');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to delete seller';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setSellerToDelete(null);
    }
  };

  // Toggle active status
  const toggleActiveStatus = async (seller) => {
    try {
      setIsLoading(true);
      const response = await putApi(`/sellers/${seller.id}`, {
        is_active: seller.is_active ? 0 : 1
      });

      if (response?.success) {
        toast.success(response.msg || 'Status updated successfully');
        if (seller.id === seller?.id) {
          // localStorage.clear()
          // navigate('/')
        }
        await fetchSellers();
      } else {
        toast.error(response?.msg || 'Failed to update status');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.msg || error.message || 'Failed to update status';
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // const handleExport =async ()=>{
  //   const idsStatus = sellers
  //   .filter(item => 'status' in item && item.status === true)
  //   .map(item => item.id);
  //   const val ={
  //     search:searchQuery,
  //     ids:idsStatus,
  //     // is_sample:1
  //   }
  //   const res = await exportShippingMethods(val,'Export_Shipping.xlsx')
  // }

  return (
    <div className="RightSideDiv sales-add">
                    {showImportModal && <Import setImportFile={setImportFile} importFile={importFile} searchQuery={searchQuery} fetchData={fetchSellers} setShowImportModal={setShowImportModal} routeEnd={'/sellers/import'} routeexport={'/sellers/export'} type={'Sales-Member'} />}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                onClick={() => setShowDeleteModal(false)}
                className="close"
                alt="Close"
              />
            </div>
            <div className="modal-body mb-0 p-0">
              <p>Are you sure you want to delete the seller: <strong>{sellerToDelete?.name}</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="reset" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button
                className="save mt-0"
                onClick={confirmDeleteSeller}
                disabled={isLoading}
              >
                {isLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sellers List */}
      {methodType === '' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right setting-right-table">
              <div className="table-header">
                <div className="search-card">
                  <img src={window.getPublicUrl('/assets/images/search-icon.svg')} alt="Search" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Sales Member..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="table-actions">
                {checkAddPermission(data?.permissions, 'sales_member') && (
                  <button className="btn" onClick={() => handleImportClickFile(setShowImportModal, setImportFile, setImportErrors)}>
                    <img src={window.getPublicUrl('/assets/images/import.png')} alt="Import" />
                    Import
                  </button>
                )}
                  <button className="btn" onClick={() => handleExportFile(sellers, searchQuery, 'sellers.xlsx', '/sellers/export')}>
                    <img src={window.getPublicUrl('/assets/images/export.svg')} alt="Export" />
                    Export
                  </button>
                  {checkAddPermission(data?.permissions, 'sales_member') && (
                    <button className="btn add" onClick={() => setMethodType('add')}>
                      + Add Sales Member
                    </button>
                  )}
                </div>
              </div>


              <div className="permission-table-inner category-table">
                <table className="custom-table table-striped">
                  <thead>
                    <tr>
                      <th className="checkbox">
                        <label className="checbox-cs">
                          <input type="checkbox" onClick={(e) => handleUpdateAllCheckbox(e?.target?.checked, setSellers)} />
                          <span className="checkmark"></span>
                        </label>
                      </th>
                      <th className="code">Sr. No.</th>
                      <th className="name">Name</th>
                      <th className="email">Email</th>
                      <th className="status">Status</th>
                      <th className="action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="6" className="text-center text-danger">{error}</td>
                      </tr>
                    ) : sellers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">No sales members found</td>
                      </tr>
                    ) : (
                      sellers.map((seller, index) => (
                        <tr key={seller.id}>
                          <td className="checkbox">
                            <label className="checbox-cs">
                              <input type="checkbox" onClick={(e) => handleUpdateDataCheckbox(seller, e, setSellers)} checked={seller?.status} />
                              <span className="checkmark"></span>
                            </label>
                          </td>
                          <td className="code">{index + 1}</td>
                          <td className="name">{seller.name}</td>
                          <td className="email">{seller.email}</td>
                          <td className="status">
                            <span className={`status-badge ${seller.is_active ? 'active' : 'inactive'}`}>
                              {seller.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="action action-cs">
                            <div className="dropdown">
                                    {(checkEditPermission(data?.permissions, 'sales_member') || checkDeletePermission(data?.permissions, 'sales_member')) && (
                            <button class=" dropdown-toggle action-btn" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                                Action <img src={window.getPublicUrl('/assets/images/action-drops.png')} alt="Actions" className="ps-2" />
                              </button>
                                    )}
                              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                                <li>
                                {checkEditPermission(data?.permissions, 'sales_member') && (
                                  <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); setupEditForm(seller); }}>
                                    Edit
                                  </a>
                                )}
                                </li>
                                <li>
                                {checkDeletePermission(data?.permissions, 'sales_member') && (
                                  <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); setSellerToDelete(seller); setShowDeleteModal(true); }}>
                                    Delete
                                  </a>
                                )}
                                </li>
                            
                              <li>
                              {checkEditPermission(data?.permissions, 'sales_member') && (
                                  <a href="#" className="dropdown-item" onClick={(e) => { e.preventDefault(); toggleActiveStatus(seller); }}>
                                    {seller.is_active ? 'Make Inactive' : 'Make Active'}
                                  </a>
                                )}
                              </li>
                              </ul>
                              </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
                   <div className="table-footer d-flex justify-content-between table-pagination">
                                {/* <select
                                  name=""
                                  id=""
                                  className="affter-change-cs page"
                                  value=''
                                  onChange=''
                                >
                                  <option value="20">50</option>
                                  <option value="15">100</option>
                                  <option value="10">250</option>
                                  <option value="5">500</option>
                                </select> */}
                                <div>
                                  {/* <TableFooter
                                    pageCount=''
                                    handlePageClick=''
                                    currentPage=''
                                  /> */}
                                </div>
                              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Seller Form */}
      {methodType === 'add' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Add Sales Member</h3>
                  <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={resetForm}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleAddSeller}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  resetForm();
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Email<strong>*</strong>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                        placeholder="Enter Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Name<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                        placeholder="Enter Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        maxLength={50}
                      />
                      {touched.name && errors.name && (
                        <div className="invalid-feedback d-block">
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
              {/* <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={resetForm}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleAddSeller}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}

      {/* Edit Seller Form */}
      {methodType === 'edit' && (
        <div className="row">
          <div className="col-sm-12">
            <div className="setting-right position-relative">
              <h3>Edit Sales Member</h3>
                    <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setFormData({
                          email: sellerToEdit?.email || '',
                          name: sellerToEdit?.name || '',
                          is_active: sellerToEdit?.is_active || 1
                        });
                        setErrors({ email: '', name: '' });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleUpdateSeller}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Updating...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
              <img
                src={window.getPublicUrl('/assets/images/close.svg')}
                className="close"
                alt="Close"
                onClick={() => {
                  setMethodType('');
                  resetForm();
                }}
              />
              <div className="setting-inner">
                <div className="row">
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Email<strong>*</strong>
                      </label>
                      <input
                        type="email"
                        className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                        placeholder="Enter Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        disabled
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">
                          {errors.email}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-6">
                    <div className="form-group">
                      <label>
                        Name<strong>*</strong>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                        placeholder="Enter Full Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        maxLength={50}
                      />
                      {touched.name && errors.name && (
                        <div className="invalid-feedback d-block">
                          {errors.name}
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
              {/* <div className="button-settings">
                <div className="col-sm-12">
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      className="reset"
                      onClick={() => {
                        setFormData({
                          email: sellerToEdit?.email || '',
                          name: sellerToEdit?.name || '',
                          is_active: sellerToEdit?.is_active || 1
                        });
                        setErrors({ email: '', name: '' });
                      }}
                    >
                      Reset
                    </button>
                    <button
                      className="save"
                      onClick={handleUpdateSeller}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Updating...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      )}


    </div>
  );
}

export default Sales;